import { INSPECTION_ITEM_STATUS_MAP } from '@/shared/constants/statusMaps';
import { InspectionItemStatus } from '@/shared/enums/InspectionStatus';

export type CardType =
  | 'awaiting_direction'
  | 'awaiting_acceptance'
  | 'in_progress'
  | 'completed'
  | 'awaiting_review'
  | 'pending'
  | 'all_inspections';

export interface InspectionItem {
  id: string;
  code: string;
  proposalNumber: string;
  status: string;
  statusLabel: string;
  createdAt: string;
  updatedAt: string;
  providerName?: string;
  inspectorName?: string;
  address?: string;
  city?: string;
  state?: string;
  scheduledDate?: string;
  priority?: 'high' | 'medium' | 'low';
  daysInStatus: number;
}

export interface CardConfig {
  id: CardType;
  title: string;
  icon: string;
  color: string;
  description: string;
  statuses: string[];
}

export const CARD_CONFIGS: Record<CardType, CardConfig> = {
  awaiting_acceptance: {
    id: 'awaiting_acceptance',
    title: 'Aguardando Aceite',
    icon: 'acceptance',
    color: 'cyan',
    description: 'Inspeções aguardando aceite da prestadora ou inspetor',
    statuses: [
      'AguardandoAceitePrestadora',
      'AguardandoAceiteInspetor',
    ],
  },
  awaiting_direction: {
    id: 'awaiting_direction',
    title: 'Aguardando Direcionamento',
    icon: 'direction',
    color: 'blue',
    description: 'Inspeções aguardando atribuição de prestadora ou inspetor',
    statuses: ['AguardandoDirecionamento', 'AguardandoDirecionamentoInspetor'],
  },
  in_progress: {
    id: 'in_progress',
    title: 'Em Andamento',
    icon: 'progress',
    color: 'orange',
    description: 'Todas as inspeções em andamento (exceto concluídas)',
    statuses: [
      'AguardandoDirecionamento',
      'AguardandoDirecionamentoInspetor',
      'AguardandoAceitePrestadora',
      'AguardandoAceiteInspetor',
      'PrestadoraRecusou',
      'InspetorRecusou',
      'AguardandoAgendamento',
      'Agendado',
      'CheckinRealizado',
      'TelevistoriaAgendada',
      'TelevistoriaFinalizada',
      'AguardandoPreenchimentoPrestadora',
      'AguardandoGeracaoLaudo',
      'AguardandoRevisaoPrestadora',
      'LaudoNegadoPorPrestadora',
      'AguardandoRevisaoBanco',
      'LaudoNegadoPorBanco',
      'CorrecaoPeloInspetorBanco',
      'LaudoDevolvido',
      'ProcessandoScraping',
      'AguardandoAnaliseIA',
      'ProcessandoAnaliseIA',
      'LaudoAutomaticoGerado',
      'ScrapingFalhou',
      'AnaliseIAFalhou',
      'AguardandoCalculosPrestadora',
      'DevolvidoPeloBancoAposAceite',
      'DevolvidoPelaPrestadora',
    ],
  },
  awaiting_review: {
    id: 'awaiting_review',
    title: 'Revisões de Laudo',
    icon: 'review',
    color: 'purple',
    description: 'Laudos em processo de revisão',
    statuses: [
      'AguardandoRevisaoPrestadora',
      'AguardandoRevisaoBanco',
      'LaudoNegadoPorBanco',
      'DevolvidoPeloBancoAposAceite',
      'DevolvidoPelaPrestadora',
    ],
  },
  pending: {
    id: 'pending',
    title: 'Pendências',
    icon: 'warning',
    color: 'red',
    description: 'Itens com pendências que requerem atenção',
    statuses: [
      'LaudoNegadoPorBanco',
      'AnaliseIAFalhou',
      'ScrapingFalhou',
    ],
  },
  completed: {
    id: 'completed',
    title: 'Concluído',
    icon: 'progress',
    color: 'green',
    description: 'Inspeções com laudo aceito pelo banco',
    statuses: ['LaudoAceitoPeloBanco'],
  },
  all_inspections: {
    id: 'all_inspections',
    title: 'Inspeções',
    icon: 'review',
    color: 'gray',
    description: 'Listagem completa de todas as inspeções',
    statuses: [],
  },
};

/**
 * STATUS_LABELS derivado da fonte centralizada INSPECTION_ITEM_STATUS_MAP.
 * Gera um mapa de nome do enum -> label para compatibilidade com código existente.
 */
export const STATUS_LABELS: Record<string, string> = Object.entries(InspectionItemStatus)
  .filter(([key]) => isNaN(Number(key)))
  .reduce((acc, [key, value]) => {
    const statusInfo = INSPECTION_ITEM_STATUS_MAP[value as InspectionItemStatus];
    if (statusInfo) {
      acc[key] = statusInfo.label;
    }
    return acc;
  }, {} as Record<string, string>);
